import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { describeError } from "@/lib/api";
import AuthShell from "@/components/layout/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInfo(data);
      } catch (e) {
        setErr(describeError(e, "This invitation link is not valid."));
      }
    })();
  }, [token]);

  const accept = async () => {
    setFieldError("");
    if (!info.has_account) {
      if (pw.length < 8) {
        setFieldError("Choose a password of at least 8 characters");
        toast.error("Choose a password of at least 8 characters");
        return;
      }
      if (pw !== confirm) {
        setFieldError("Both passwords must match");
        toast.error("Both passwords must match");
        return;
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/invite/${token}/accept`,
        info.has_account ? {} : { password: pw });
      setUser(data.user);
      toast.success("Welcome to your client portal");
      navigate("/portal");
    } catch (e) {
      toast.error(describeError(e, "Could not accept this invitation."));
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <AuthShell title="Invitation unavailable" subtitle={err}>
        <div data-testid="invite-invalid" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Invitation links expire and can be used only once. Please ask your advocate to send a new one.
          </p>
          <Button onClick={() => navigate("/login")} data-testid="invite-goto-login"
                  className="w-full bg-navy text-white hover:bg-navy-light">
            Go to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (!info) {
    return (
      <AuthShell title="Checking your invitation" subtitle="One moment please.">
        <div data-testid="invite-loading" className="h-24 rounded bg-secondary" />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Your client portal"
      subtitle={`${info.firm_name} has invited you to follow your matter.`}
    >
      <div data-testid="invite-accept-form" className="space-y-4">
        <div className="rounded-md bg-secondary/60 px-3 py-2 text-sm text-navy" data-testid="invite-summary">
          <p><span className="text-muted-foreground">Client:</span> {info.client_name}</p>
          <p><span className="text-muted-foreground">Email:</span> {info.email}</p>
        </div>

        {info.has_account ? (
          <p className="text-sm text-muted-foreground" data-testid="invite-has-account">
            You already have an account with this email. Accept to link this matter to it.
          </p>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="invite-pw">Choose a password</Label>
              <Input id="invite-pw" type="password" value={pw} data-testid="invite-password-input"
                     onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-pw2">Confirm password</Label>
              <Input id="invite-pw2" type="password" value={confirm} data-testid="invite-confirm-input"
                     onChange={(e) => setConfirm(e.target.value)} />
            </div>
          </>
        )}

        {fieldError && (
          <p data-testid="invite-field-error" role="alert" className="text-sm text-destructive">
            {fieldError}
          </p>
        )}

        <Button onClick={accept} disabled={busy} data-testid="invite-accept-btn"
                className="w-full bg-gold text-navy hover:bg-gold-dark">
          {busy ? "Please wait…" : info.has_account ? "Accept invitation" : "Create my portal access"}
        </Button>
      </div>
    </AuthShell>
  );
}
